import api from "@/_helpers/api";
import router from "@/_helpers/router.ts";
import { useAccountStore } from "@/_store/account.module";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import type { WorkspaceDetails } from "@/_store/workspaces-list.module.ts";
import type { SetupHubStepItem } from "@/_store/setup-hub.module.ts";
import { SetupHubStep } from "@/constants/setup-hub.ts";
import { RouteName } from "@/constants/routes.ts";
import { SubscriptionModule } from "@/constants/workplaces";
import { isModuleDisabled } from "@/_helpers/subscription";
import { useResetStore } from "@/_helpers/workspace";
import { type LocationQueryRaw } from "vue-router";

/**
 * Navigates to a route with a full page reload (window.location).
 * Use when a hard reload is needed to reinitialize the app context.
 * @param routeName - The route to navigate to.
 */
export const navigateWithReload = (routeName: RouteName) => {
  const resolvedRoute = router.resolve({ name: routeName });
  window.location.href = resolvedRoute.href;
};

/**
 * Navigates to a route while updating workspace and permissions.
 * Used for MSP workspace navigation to switch workspace context.
 * @sideEffects — updates workspace store, resets Pinia stores, checks MSP access
 */
export const navigateToRoute = async (
  workspaceId: string,
  routeName: string,
  query: LocationQueryRaw = {}
) => {
  const baseWorkspaceStore = useWorkspaceStore();
  await baseWorkspaceStore.updateWorkspaceAndPermissions(workspaceId, false);
  useResetStore().allExceptIgnoreList();
  await useAccountStore().checkMspAccess();
  await router.push({ name: routeName, query });
};

/**
 * Fetches workspace details for a given workspaceId from the API.
 * Uses raw fetch (not axios) to avoid the auth interceptor loop on 401.
 * @sideEffects — makes HTTP request
 * @param workspaceId - The workspace ID to look up.
 * @returns Workspace details or null if not found / request fails.
 */
export const getWorkspaceFromRedirect = async (
  workspaceId: string
): Promise<WorkspaceDetails | null> => {
  if (!workspaceId) {
    return null;
  }

  const resultUrl = `${import.meta.env.VITE_API_HOST}${api.getCurrentWorkspace.url}`;

  try {
    const data = await fetch(resultUrl, {
      headers: {
        Authorization: `Bearer ${useAccountStore().token}`,
        Workspace: workspaceId,
      },
    });

    if (!data.ok) {
      return null;
    }

    return data.json();
  } catch (e) {
    console.error(e);
    return null;
  }
};

/**
 * Handles router navigation for a given Setup Hub step item.
 * Each step maps to a specific route in the app.
 * @sideEffects — calls router.push
 * @param item - The Setup Hub step item to navigate for.
 */
export const handleNavigationInSetupHub = (item: SetupHubStepItem) => {
  switch (item.step) {
    case SetupHubStep.INVITE_TEAMMATES:
      router.push({ name: RouteName.ACCESS_CONTROL_ADMIN_USERS_TAB });
      break;
    case SetupHubStep.CONNECT_CLOUD_APP:
      router.push({ name: RouteName.CLOUD_APPS_PAGE });
      break;
    case SetupHubStep.ADD_PROTECTED_USERS:
      router.push({ name: RouteName.USERS_SETTINGS_USERS_TAB });
      break;
    case SetupHubStep.SETUP_ACCESS_PERMISSIONS:
      router.push({ name: RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB });
      break;
    case SetupHubStep.DEPLOY_ENDPOINT_AGENT:
      router.push({ name: RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB });
      break;
    case SetupHubStep.ACTIVATE_SAT:
      router.push({ name: RouteName.SECURITY_AWARENESS_SETTINGS });
      break;
    case SetupHubStep.ADD_DEVICE_LABELS:
      router.push({ name: RouteName.DEVICES_SETTINGS_LABELS_TAB });
      break;
    case SetupHubStep.ADD_DEVICE_POSTURE_SETTINGS:
      router.push({ name: RouteName.ENDPOINT_SECURITY_DEVICE_POSTURE_TAB });
      break;
    case SetupHubStep.DEFINE_DEVICE_DATA_MONITORING_RULES:
      router.push({ name: RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB });
      break;
    case SetupHubStep.DEFINE_USER_DATA_MONITORING_RULES:
      router.push({ name: RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB });
      break;
    case SetupHubStep.CREATE_SWG_VIRTUAL_SERVER:
      router.push({ name: RouteName.NETWORK_SETTINGS_TAB });
      break;
    case SetupHubStep.CREATE_NETWORK_VIRTUAL_SERVER:
      if (isModuleDisabled(SubscriptionModule.NETWORK)) {
        router.push({ name: RouteName.SECURE_WEB_GATEWAY_SETTINGS });
        return;
      }
      router.push({ name: RouteName.NETWORK_SETTINGS_TAB });
      break;
    case SetupHubStep.SETUP_VPN_ACCESS:
      router.push({ name: RouteName.NETWORK_VPN_TAB });
      break;
    case SetupHubStep.ENABLE_DNS_FILTERING:
      router.push({ name: RouteName.SECURE_WEB_GATEWAY_SETTINGS });
      break;
  }
};
